import React, { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addMonths, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import api from '@/lib/api';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CalendarView.css';

const locales = {
  'pt-BR': ptBR,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
  getDay,
  locales,
});

const messages = {
  allDay: 'Dia inteiro',
  previous: 'Anterior',
  next: 'Próximo',
  today: 'Hoje',
  month: 'Mês',
  week: 'Semana',
  day: 'Dia',
  agenda: 'Agenda',
  date: 'Data',
  time: 'Hora',
  event: 'Evento',
  noEventsInRange: 'Não há licenças neste período',
  showMore: (total) => `+ ${total} mais`,
};

const CALENDAR_VIEWS = ['month', 'agenda'];

function CalendarView({ user }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('month');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [projects, setProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState('all');

  useEffect(() => {
    fetchProjects();
    fetchEvents();
  }, [statusFilter, projectFilter]);

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchEvents = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status_filter', statusFilter);
      if (projectFilter !== 'all') params.append('project_id', projectFilter);

      const response = await api.get(`/calendar/events?${params}`);

      const formattedEvents = response.data.map((event) => ({
        ...event,
        start: new Date(event.start),
        end: new Date(event.end),
      }));

      setEvents(formattedEvents);
    } catch (error) {
      console.error('Error fetching calendar events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setDialogOpen(true);
  };

  const eventStyleGetter = (event) => {
    const style = {
      backgroundColor: event.resource.color,
      borderRadius: '4px',
      opacity: 0.9,
      color: 'white',
      border: '0px',
      display: 'block',
      fontSize: '12px',
      padding: '2px 5px',
    };
    return { style };
  };

  const getStatusBadge = (status) => {
    const styles = {
      valid: 'bg-green-100 text-green-700',
      expiring: 'bg-yellow-100 text-yellow-700',
      critical: 'bg-orange-100 text-orange-700',
      expired: 'bg-red-100 text-red-700',
    };
    const labels = {
      valid: 'Válida',
      expiring: 'Expirando',
      critical: 'Crítica',
      expired: 'Expirada',
    };
    return (
      <Badge className={styles[status] || 'bg-gray-100 text-gray-700'}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-800"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-gray-900">Calendário de Renovações</h1>
            <p className="text-gray-600 mt-1">Visualize e gerencie vencimentos de licenças</p>
          </div>

          <div className="flex items-center space-x-3">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos Status</SelectItem>
                <SelectItem value="valid">Válidas</SelectItem>
                <SelectItem value="expiring">Expirando</SelectItem>
                <SelectItem value="critical">Críticas</SelectItem>
                <SelectItem value="expired">Expiradas</SelectItem>
              </SelectContent>
            </Select>

            {user?.role !== 'CLIENT' && (
              <Select value={projectFilter} onValueChange={setProjectFilter}>
                <SelectTrigger className="w-48" data-testid="project-filter">
                  <SelectValue placeholder="Projeto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos Projetos</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.project_id} value={project.project_id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4 border border-green-200 bg-green-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700 font-medium">Válidas</p>
                <p className="text-2xl font-bold text-green-900 mt-1">
                  {events.filter((e) => e.resource.status === 'valid').length}
                </p>
              </div>
              <div className="w-10 h-10 bg-green-200 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </Card>

          <Card className="p-4 border border-yellow-200 bg-yellow-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-700 font-medium">Expirando (30-90d)</p>
                <p className="text-2xl font-bold text-yellow-900 mt-1">
                  {events.filter((e) => e.resource.status === 'expiring').length}
                </p>
              </div>
              <div className="w-10 h-10 bg-yellow-200 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-yellow-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </Card>

          <Card className="p-4 border border-orange-200 bg-orange-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-700 font-medium">Críticas (&lt;30d)</p>
                <p className="text-2xl font-bold text-orange-900 mt-1">
                  {events.filter((e) => e.resource.status === 'critical').length}
                </p>
              </div>
              <div className="w-10 h-10 bg-orange-200 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
            </div>
          </Card>

          <Card className="p-4 border border-red-200 bg-red-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-700 font-medium">Expiradas</p>
                <p className="text-2xl font-bold text-red-900 mt-1">
                  {events.filter((e) => e.resource.status === 'expired').length}
                </p>
              </div>
              <div className="w-10 h-10 bg-red-200 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-red-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            </div>
          </Card>
        </div>

        <Card className="p-6 border border-gray-200">
          <div style={{ height: 600 }}>
            <Calendar
              localizer={localizer}
              events={events}
              startAccessor="start"
              endAccessor="end"
              style={{ height: '100%' }}
              onSelectEvent={handleSelectEvent}
              eventPropGetter={eventStyleGetter}
              messages={messages}
              culture="pt-BR"
              views={CALENDAR_VIEWS}
              view={view}
              onView={setView}
              date={currentDate}
              onNavigate={setCurrentDate}
            />
          </div>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Detalhes da Licença</DialogTitle>
            </DialogHeader>
            {selectedEvent && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Nome da Licença</p>
                    <p className="font-semibold text-gray-900">{selectedEvent.title}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <div className="mt-1">{getStatusBadge(selectedEvent.resource.status)}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Número</p>
                    <p className="font-medium text-gray-900">{selectedEvent.resource.license_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Órgão Emissor</p>
                    <p className="font-medium text-gray-900">{selectedEvent.resource.issuing_authority}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Projeto</p>
                    <p className="font-medium text-gray-900">{selectedEvent.resource.project_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Cliente</p>
                    <p className="font-medium text-gray-900">{selectedEvent.resource.client_name}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Data de Vencimento</p>
                    <p className="font-medium text-gray-900">
                      {new Date(selectedEvent.start).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Dias até Vencimento</p>
                    <p className={`font-bold text-lg ${
                      selectedEvent.resource.days_until_expiration < 0
                        ? 'text-red-600'
                        : selectedEvent.resource.days_until_expiration < 30
                        ? 'text-orange-600'
                        : selectedEvent.resource.days_until_expiration < 90
                        ? 'text-yellow-600'
                        : 'text-green-600'
                    }`}>
                      {selectedEvent.resource.days_until_expiration < 0
                        ? `Expirada há ${Math.abs(selectedEvent.resource.days_until_expiration)} dias`
                        : `${selectedEvent.resource.days_until_expiration} dias`}
                    </p>
                  </div>
                </div>

                {selectedEvent.resource.notes && (
                  <div>
                    <p className="text-sm text-gray-500">Observações</p>
                    <p className="mt-1 text-gray-900">{selectedEvent.resource.notes}</p>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => window.open(`/projects/${selectedEvent.resource.project_id}`, '_blank')}
                  >
                    Ver Projeto
                  </Button>
                  <Button onClick={() => setDialogOpen(false)} className="bg-emerald-800 hover:bg-emerald-900">
                    Fechar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default CalendarView;